import type { JSX } from 'react';
import { WhatsAppIcon } from '@components/icons/WhatsAppIcon';
import { Button } from '@components/ui/Button';
import { Container } from '@components/ui/Container';
import { SectionHeading } from '@components/ui/SectionHeading';
import { landingCopy } from '@features/landing/data/landing.pt';
import { trackWhatsAppClick } from '@lib/analytics';
import { whatsappLink } from '@lib/whatsappLink';

import { HeroMockupPlaceholder } from './HeroMockupPlaceholder';

const HERO_SOURCE_ID = 'hero-cta';
// Anchor target lands in Phase 4 (SECT-05). Harmless until then — clicking
// is a no-op (no console error, no scroll). Documented in CONTEXT.md
// "Claude's Discretion" + threat-model T-06-07 (accepted).
const SECONDARY_CTA_HREF = '#como-funciona';

/**
 * Hero — primeira dobra conversora da landing Omanna (SECT-01).
 *
 * Composto de:
 *  - `<section>` wrapper (background, overflow-hidden, padding vertical,
 *    hospeda o blob decorativo absoluto)
 *  - Forma decorativa organica `bg-brand-100/60` (`aria-hidden`, absolute)
 *    como SIBLING do `<Container>` — INSIDE da `<section>` mas FORA do
 *    max-width do Container
 *  - `<Container>` (max-w-7xl + padding horizontal) wrappando APENAS o
 *    conteudo grid 2 colunas
 *  - Coluna esquerda: `<SectionHeading>` (h1 + subtitle) + linha de CTAs
 *  - Coluna direita: `<HeroMockupPlaceholder>` (swappable quando o mockup
 *    real chegar)
 *
 * Decisao estrutural (W-10): a section element envolve o Container em vez
 * de o Container assumir a tag semantica. O blob decorativo precisa estender
 * alem do `max-w-7xl` do Container para ancorar a identidade visual na
 * borda do viewport. Se o Container fosse a section, o `overflow-hidden`
 * cortaria o blob no max-width. Outras secoes (Phases 3-5) sem blob
 * absolute-positioned podem assumir a tag section via prop polimorfica.
 *
 * Todo texto vem de `landingCopy.hero.*` — JSX nao tem string PT-BR
 * hardcoded (clean-code §13, PRD §9). O CTA primario abre
 * `wa.me/<numero>?text=<msg>` em nova aba (PRD §5).
 *
 * Security: `target="_blank"` + `rel="noopener noreferrer"` no CTA primario
 * mitigam reverse tabnabbing (T-06-01). React escapa as strings da
 * `landingCopy` automaticamente — toda renderizacao usa interpolacao JSX
 * segura, nunca injecao de HTML cru (T-06-03).
 */
export const Hero = (): JSX.Element => {
  const whatsappHref = whatsappLink(landingCopy.whatsapp.defaultMessage, HERO_SOURCE_ID);

  return (
    <section
      id="hero"
      className="relative overflow-hidden bg-neutral-50 pt-24 pb-20 lg:pt-32 lg:pb-24"
    >
      {/*
        Decorative organic blob — purely visual; SIBLING of Container, INSIDE
        the section's overflow-hidden boundary. See component JSDoc for why
        Container does NOT wrap this element.
      */}
      <div
        aria-hidden="true"
        className="absolute -top-20 -right-32 w-72 h-72 lg:top-[-80px] lg:right-[-120px] lg:w-[480px] lg:h-[480px] z-0 bg-brand-100/60 rounded-[50%_30%_60%_40%/40%_60%_30%_70%]"
      />

      {/*
        Container = max-w-7xl + px-6 lg:px-8 wrapping ONLY the content grid.
        Default as='div' here (NOT as='section') — see component JSDoc above
        for the W-10 rationale.
      */}
      <Container className="relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-[1.2fr_1fr] lg:gap-12 lg:items-center">
          <div>
            <SectionHeading
              as="h1"
              title={landingCopy.hero.headline}
              subtitle={landingCopy.hero.subtitle}
              align="left"
            />

            <div className="mt-10 flex flex-col sm:flex-row gap-4 sm:items-center">
              <Button
                variant="primary"
                size="lg"
                as="a"
                href={whatsappHref}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={landingCopy.hero.primaryCtaAriaLabel}
                icon={<WhatsAppIcon className="h-5 w-5" />}
                onClick={() => {
                  trackWhatsAppClick(HERO_SOURCE_ID);
                }}
              >
                {landingCopy.hero.primaryCtaLabel}
              </Button>

              <Button
                variant="ghost"
                size="md"
                as="a"
                href={SECONDARY_CTA_HREF}
                aria-label={landingCopy.hero.secondaryCtaAriaLabel}
              >
                {landingCopy.hero.secondaryCtaLabel}
              </Button>
            </div>
          </div>

          <div className="mt-12 lg:mt-0 flex justify-center lg:justify-end">
            <HeroMockupPlaceholder ariaLabel={landingCopy.hero.mockupAlt} />
          </div>
        </div>
      </Container>
    </section>
  );
};
